import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, scanUrl } from "../lib/api";
import { Search, RefreshCw, ShieldCheck, Monitor } from "lucide-react";

const VERDICT_BG = { safe: "#00B259", suspicious: "#FFC400", dangerous: "#E53935" };
const VERDICT_FG = { safe: "#fff", suspicious: "#050505", dangerous: "#fff" };

function Badge({ verdict }) {
  return (
    <span
      className="text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-1 border-2 border-black"
      style={{ background: VERDICT_BG[verdict], color: VERDICT_FG[verdict] }}
      data-testid={`my-badge-${verdict}`}
    >
      {verdict}
    </span>
  );
}

const DEVICE_KEY = "wg_device_id_client";

export default function MyDashboard() {
  const [sp] = useSearchParams();
  const initialDid = sp.get("d") || localStorage.getItem(DEVICE_KEY) || "";
  const [deviceId, setDeviceId] = useState(initialDid);
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (sp.get("d")) localStorage.setItem(DEVICE_KEY, sp.get("d"));
  }, [sp]);

  const load = async (did) => {
    if (!did) return;
    setLoading(true); setError("");
    try {
      const [s, l] = await Promise.all([
        api.get(`/my/stats`, { params: { device_id: did } }).then((r) => r.data),
        api.get(`/my/scans`, { params: { device_id: did, limit: 50 } }).then((r) => r.data),
      ]);
      setStats(s); setScans(l);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (deviceId) load(deviceId); }, [deviceId]);

  const onScan = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setScanning(true);
    try {
      await scanUrl(url.trim(), deviceId);
      setUrl("");
      await load(deviceId);
    } finally { setScanning(false); }
  };

  if (!deviceId) {
    return (
      <div className="max-w-[700px] mx-auto px-6 py-16">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Personal Dashboard</div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Clash Display'" }}>Link your extension</h1>
        <p className="mt-3 text-sm text-neutral-700">
          This dashboard shows only the scans performed on <strong>this computer</strong>. To link it, install the WallGard Chrome extension and click <strong>Open Dashboard</strong> in its popup — your personal link will bring you back here automatically.
        </p>
        <div className="mt-6 flex gap-3">
          <Link to="/install" className="brutal-btn" data-testid="my-install-link">Install Extension →</Link>
          <Link to="/" className="brutal-btn brutal-btn-outline">Home</Link>
        </div>
      </div>
    );
  }

  const didShort = deviceId.slice(0, 8) + "…" + deviceId.slice(-4);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="my-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 flex items-center gap-2">
            <Monitor size={12}/> This device · {didShort}
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "'Clash Display'" }}>My Scans</h1>
        </div>
        <button onClick={() => load(deviceId)} className="brutal-btn brutal-btn-outline" data-testid="my-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && <div className="mt-4 border-2 border-black bg-[#E53935] text-white p-3 text-xs font-bold tracking-widest uppercase">{error}</div>}

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border-2 border-black bg-white" data-testid="my-stats-grid">
        {[
          { label: "My Scans", value: stats?.total ?? 0, bg: "#fff", fg: "#050505" },
          { label: "Genuine", value: stats?.safe ?? 0, bg: "#00B259", fg: "#fff" },
          { label: "Suspicious", value: stats?.suspicious ?? 0, bg: "#FFC400", fg: "#050505" },
          { label: "Threats Blocked", value: stats?.dangerous ?? 0, bg: "#E53935", fg: "#fff" },
        ].map((s, i) => (
          <div key={i} className={`p-6 ${i < 3 ? "border-r-2 border-black" : ""}`} style={{ background: s.bg, color: s.fg }} data-testid={`my-stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{s.label}</div>
            <div className="text-5xl font-black tracking-tighter mt-2" style={{ fontFamily: "'Clash Display'" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <form onSubmit={onScan} className="mt-8 brutal-card p-2 flex items-center gap-2" data-testid="my-scan-form">
        <Search size={18} className="ml-2" />
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Scan a URL from this computer…"
          className="flex-1 px-2 py-3 outline-none bg-transparent font-mono text-sm"
          data-testid="my-scan-input"
        />
        <button className="brutal-btn" disabled={scanning} data-testid="my-scan-btn">{scanning ? "Scanning…" : "Scan"}</button>
      </form>
      {scanning && <div className="scan-stripes h-2 mt-2" />}

      {stats && Object.keys(stats.categories || {}).length > 0 && (
        <div className="mt-8 brutal-card p-6" data-testid="my-categories-block">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
            <ShieldCheck size={12}/> Threats I Encountered
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.categories).map(([k, v]) => (
              <div key={k} className="border-2 border-black px-3 py-2 flex items-center gap-3">
                <span className="text-[10px] tracking-widest uppercase font-bold">{k}</span>
                <span className="bg-black text-white px-2 py-0.5 text-xs font-bold">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 brutal-card overflow-x-auto" data-testid="my-scans-table">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-neutral-100">
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Time</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Verdict</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Host</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black border-r-2">Risk</th>
              <th className="text-left text-[10px] uppercase tracking-[0.2em] p-3 border-b-2 border-black">Reason</th>
            </tr>
          </thead>
          <tbody>
            {scans.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-sm text-neutral-500">{loading ? "Loading…" : "No scans yet. Browse some sites with the WallGard extension installed and they'll show up here."}</td></tr>
            )}
            {scans.map((s) => (
              <tr key={s.id} data-testid={`my-scan-row-${s.id}`}>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20 font-mono text-xs">{new Date(s.timestamp).toLocaleString()}</td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20"><Badge verdict={s.verdict} /></td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20 font-mono text-xs max-w-[260px] truncate">{s.host}</td>
                <td className="p-3 border-b border-black/20 border-r-2 border-r-black/20 font-bold">{s.risk}</td>
                <td className="p-3 border-b border-black/20 text-xs max-w-[420px]">{s.ai_reason || s.findings?.[0]?.detail || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-neutral-400">
        Only this computer's scans are shown. Your device ID is stored locally and never shared.
      </div>
    </div>
  );
}
